import { t as __t } from "i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import "../i18n/lang";
import HeaderNavContent from "./HeaderNavContent";
import { handleGetCookie } from "../../utils/commonUtil";
import Lang from "../common/dropdown-lang/dropdownLang";
import Swal from "sweetalert2";
import "./dashboard-shell-green.css";
import { callSearchUser } from "../../api/callcenters/service";
import UserProfileModal from "./UserProfileModal";

const DashboardHeader = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const [scrolled, setScrolled] = useState(false);
	const [user, setUser] = useState(null);
	const [showProfileModal, setShowProfileModal] = useState(false);
	const [profileUser, setProfileUser] = useState(null);
	const [profileLoading, setProfileLoading] = useState(false);

	useEffect(() => {
		const authData = handleGetCookie("auth");
		if (authData?.user?.userId) {
			setUser(authData.user);
		}

		const handleScroll = () => setScrolled(window.scrollY > 8);
		handleScroll();
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const displayName = useMemo(() => {
		if (!user?.userId) return "G-Win";
		return `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "G-Win";
	}, [user]);

	const initials = useMemo(() => {
		const parts = displayName.split(" ").filter(Boolean);
		return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("") || "GW";
	}, [displayName]);

	const pageLabel = useMemo(() => {
		if (pathname.includes("dashboard")) return "Dashboard";
		if (pathname.includes("notification")) return "Notifications";
		if (pathname.includes("setting")) return "Settings";
		return "Management System";
	}, [pathname]);


	const handleOpenProfile = async () => {
		const authData = handleGetCookie("auth");
		const currentUser = authData?.user || user;

		setProfileUser(currentUser || null);
		setShowProfileModal(true);

		if (!currentUser?.userId) return;

		setProfileLoading(true);
		try {
			const rsp = await dispatch(
				callSearchUser({
					page: 1,
					maxResult: 1,
					keyword: "",
					userId: currentUser.userId,
				})
			);

			if (rsp?.payload?.responseCode === "00000") {
				const rows = rsp?.payload?.data?.result || [];
				const exactUser = rows.find((item) => Number(item.userId) === Number(currentUser.userId)) || rows[0];
				if (exactUser) {
					setProfileUser({ ...currentUser, ...exactUser });
				}
			}
		} catch (error) {
			console.log("Load header user profile error:", error);
		} finally {
			setProfileLoading(false);
		}
	};

	const handleLogout = () => {
		Swal.fire({
			title: __t("uiText.text006"),
			text: __t("uiText.text007"),
			icon: "question",
			reverseButtons: true,
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#dc2626",
			confirmButtonText: __t("uiText.text008"),
			cancelButtonText: __t("uiText.text009"),
		}).then((result) => {
			if (result.isConfirmed) navigate("/logout");
		});
	};

	return (
		<header className={`main-header header-shaddow gwin-dashboard-header ${scrolled ? "fixed-header is-scrolled" : ""}`}>
			<div className="container-fluid gwin-header-container">
				<div className="main-box gwin-header-shell">
					<div className="nav-outer gwin-header-left">
						<div className="logo-box gwin-logo-box">
							<Link to="/admin/dashboard" className="gwin-brand-link" aria-label={t("accessibility.dashboard")}>
								<img src="/images/lti_logo.png" alt="brand" className="header-logo gwin-header-logo" />
								<div className="gwin-brand-copy">
									<span className="gwin-brand-title">LTI Insurance</span>
									<span className="gwin-brand-subtitle">{pageLabel}</span>
								</div>
							</Link>
						</div>
						<HeaderNavContent />
					</div>

					<div className="outer-box gwin-header-actions">
						<div className="gwin-lang-shell"><Lang /></div>
						<div className="dropdown dashboard-option gwin-user-menu">
							<button
								type="button"
								className="dropdown-toggle gwin-user-trigger"
								data-bs-toggle="dropdown"
								aria-expanded="false"
							>
								<span className="gwin-user-avatar">{initials}</span>
								<span className="gwin-user-copy">
									<span className="gwin-user-name">{displayName}</span>
									<span className="gwin-user-role">{__t("uiText.text010")}</span>
								</span>
								<i className="la la-angle-down gwin-user-chevron" />
							</button>

							<ul className="dropdown-menu dropdown-menu-end gwin-user-dropdown">
								<li>
									<button className="gwin-profile-menu-button" onClick={handleOpenProfile}>
										<i className="la la-user-circle" />
										<span>{t("callcenter.customerService.userInformation")}</span>
									</button>
								</li>
								<li><hr className="dropdown-divider gwin-user-dropdown-divider" /></li>
								<li>
									<button className="gwin-logout-button" onClick={handleLogout}>
										<i className="la la-sign-out" />
										<span>{__t("uiText.text011")}</span>
									</button>
								</li>
							</ul>
						</div>

						<button
							type="button"
							className="gwin-desktop-logout-button"
							onClick={handleLogout}
							aria-label={__t("uiText.text011")}
							title={__t("uiText.text011")}
						>
							<i className="la la-power-off" aria-hidden="true" />
						</button>
					</div>
				</div>
			</div>
			<UserProfileModal
				show={showProfileModal}
				onHide={() => setShowProfileModal(false)}
				user={profileUser}
				loading={profileLoading}
			/>
		</header>
	);
};

export default DashboardHeader;
